'use client';

import { useId, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { RiCloseLine, RiSearchLine } from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { RiArrowRightSLine } from '@/components/ui/rtl-icons';
import { Link } from '@/i18n/navigation';
import type { HelpCardView, HelpGroupView } from '@/lib/help/catalog';
import { spring, springFade, stagger } from '@/lib/motion';

/**
 * Yardım merkezinin giriş sayfası — on kategori, otuz sekiz konu, hepsi
 * AYNI EKRANDA.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden gizlenen bir kategori yok
 * ═══════════════════════════════════════════════════════════════════════════
 * Kategoriler akordiyon olabilirdi ve sayfa kısalırdı. Olmadı: bir yardım
 * merkezine gelen kişi çoğu zaman aradığı şeyin ADINI bilmiyor — "şu ekranda
 * şu düğme" diye biliyor. Kapalı bir başlık, bilmediği kelimeyi tahmin
 * etmesini ister; açık bir liste, gözüyle taramasına izin verir. Sayfanın
 * uzunluğu bunun bedeli ve kabul edildi.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Arama İSTEMCİDE — ve sunucuya hiç gitmiyor
 * ═══════════════════════════════════════════════════════════════════════════
 * Katalog otuz sekiz kayıt ve başlık+özet zaten bu sayfada. Bir arama ucu
 * yazmak, her tuşa basışta ağa çıkmak ve gecikme yönetmek demekti; kazanılan
 * hiçbir şey yok. Süzgeç `useMemo` ile çalışıyor.
 *
 * 🔴 Arama TÜRKÇE HARFLERİ KATLIYOR: "butce" yazan kişi "bütçe" konusunu
 * bulmalı. Kullanıcı panelde gördüğü kelimeyi çoğu zaman aksansız yazıyor ve
 * düz bir `toLowerCase()` onu boş sonuca gönderirdi.
 *
 * Adrese yazılmıyor (`?q=`) bilerek: bu bir sonuç sayfası değil, aynı listeyi
 * daraltan bir kutu. Adres kirletmek, geri tuşunu her tuşa basışta bir adım
 * geriye alırdı.
 */

/** Türkçe harfleri ASCII karşılığına indiren katlama. Arama iki yönde de bunu geçiyor. */
const FOLD: Record<string, string> = {
  ç: 'c',
  ğ: 'g',
  ı: 'i',
  i: 'i',
  ö: 'o',
  ş: 's',
  ü: 'u',
  â: 'a',
  î: 'i',
  û: 'u',
};

function fold(value: string): string {
  return value
    .toLowerCase()
    .replace(/[çğıiöşüâîû]/g, (char) => FOLD[char] ?? char)
    .trim();
}

const sectionV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight, delayChildren: 0.04 } },
};

const headV: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

const cardV: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

export function HelpHub({ groups }: { groups: readonly HelpGroupView[] }) {
  const t = useTranslations('help');
  const uid = useId();
  const [query, setQuery] = useState('');

  const needle = fold(query);

  /* Kategori başlığı da aranıyor: "kreatif" yazan kişi o kategorinin bütün
     konularını görmeli, yalnız başlığında o kelime geçenleri değil. */
  const haystacks = useMemo(
    () =>
      new Map(
        groups.flatMap((group) => {
          const groupText = fold(t(`categories.${group.id}.title`));
          return group.articles.map(
            (article) =>
              [
                article.slug,
                `${groupText} ${fold(article.title)} ${fold(article.summary)} ${fold(article.where ?? '')}`,
              ] as const,
          );
        }),
      ),
    [groups, t],
  );

  const filtered = useMemo(() => {
    if (needle.length === 0) return groups;

    return groups
      .map((group) => ({
        id: group.id,
        articles: group.articles.filter((article) =>
          (haystacks.get(article.slug) ?? '').includes(needle),
        ),
      }))
      .filter((group) => group.articles.length > 0);
  }, [groups, haystacks, needle]);

  const total = filtered.reduce((sum, group) => sum + group.articles.length, 0);

  return (
    <section className="hub" aria-labelledby={`${uid}-title`}>
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <div className="hub-inner">
            <div className="hub-search">
              <RiSearchLine className="hub-search-icon" size={18} aria-hidden="true" />
              <input
                className="hub-search-input"
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={t('search.placeholder')}
                aria-label={t('search.label')}
              />
              {/* Temizleme düğmesi HEP basılıyor, koşullu render edilmiyor:
                  görünürlüğü `data-on` + CSS belirliyor. Koşullu render,
                  kutu doldukça düzeni bir piksel oynatırdı. */}
              <button
                type="button"
                className="hub-search-clear"
                data-on={query.length > 0 || undefined}
                onClick={() => setQuery('')}
                aria-label={t('search.clear')}
                tabIndex={query.length > 0 ? 0 : -1}
              >
                <RiCloseLine size={16} aria-hidden="true" />
              </button>
            </div>

            {/* Sonuç sayısı canlı bölge: ekran okuyucu kullanan biri yazdıkça
                kaç konunun kaldığını duymalı — görsel kullanıcı bunu zaten
                listenin kısalmasından görüyor. */}
            <p className="hub-count" role="status" aria-live="polite">
              {query.length > 0
                ? t('search.results', { count: total })
                : t('count', { count: total })}
            </p>

            <h2 className="sr-only" id={`${uid}-title`}>
              {t('nav.label')}
            </h2>

            {filtered.length === 0 ? (
              <div className="hub-empty">
                <p className="hub-empty-title">{t('search.emptyTitle')}</p>
                <p className="hub-empty-note">{t('search.emptyNote')}</p>
              </div>
            ) : (
              filtered.map((group) => (
                <motion.section
                  className="hub-group"
                  key={group.id}
                  aria-labelledby={`${uid}-${group.id}`}
                  variants={sectionV}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.1 }}
                >
                  <motion.div className="hub-group-head" variants={headV}>
                    <h3 className="hub-group-title" id={`${uid}-${group.id}`}>
                      {t(`categories.${group.id}.title`)}
                    </h3>
                    <p className="hub-group-desc">{t(`categories.${group.id}.desc`)}</p>
                  </motion.div>

                  <ul className="hub-list">
                    {group.articles.map((article) => (
                      <HubCard key={article.slug} article={article} />
                    ))}
                  </ul>
                </motion.section>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Tek konu kartı.
 *
 * Kartın TAMAMI bağlantı — içinde ikinci bir tıklama hedefi yok. Hover'da
 * yalnız ok kımıldıyor, kart değil: otuz sekiz kartlık bir listede her
 * kartın zıpladığı bir sayfa "hassas" değil "kıpır kıpır" okunur
 * (DESIGN.md §4).
 */
function HubCard({ article }: { article: HelpCardView }) {
  const t = useTranslations('help');

  return (
    <motion.li className="hub-item" variants={cardV}>
      <Link className="hub-card" href={`/help/${article.slug}`}>
        <span className="hub-card-copy">
          <span className="hub-card-title">{article.title}</span>
          <span className="hub-card-summary">{article.summary}</span>
          {article.where ? (
            <span className="hub-card-where">
              <span className="hub-card-where-label">{t('where')}</span>
              {article.where}
            </span>
          ) : null}
        </span>
        <RiArrowRightSLine className="hub-card-arrow" size={18} aria-hidden="true" />
      </Link>
    </motion.li>
  );
}
